import { useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { websiteUrl } from "../constants/config";
import { SurveyShareButtonProps } from "../pages/view-survey/components/share-survey-button.component";
import { showNotification } from "../redux/notification/notification.slice";
import { useShareSurveyResultsMutation } from "../redux/survey/survey.endpoints";
import { SurveyStatus } from "../redux/survey/survey.types";
import { useCopy } from "./useCopy";
import ErrorLogService from "../services/error-log/error-log.service";

const copySuccessMessage = 'Survey link copied.';

interface UseShareSurveyReturn {
  readonly canShare: boolean;
  readonly handleShare: () => void;
  readonly isLoading: boolean;
}

const useShareSurvey = ({ surveyId, surveyStatus }: SurveyShareButtonProps): UseShareSurveyReturn => {
  const shareUrl = useRef('');
  const canShare = surveyStatus === SurveyStatus.Live || surveyStatus === SurveyStatus.Complete;
  const [shareSurvey, { data, error, isError, isLoading }] = useShareSurveyResultsMutation();
  const { toClipboard } = useCopy();
  const dispatch = useDispatch();

  useEffect(() => {
    if (data && !shareUrl.current) {
      shareUrl.current = createUrl(data);
    }

    shareUrl.current && void toClipboard(shareUrl.current, copySuccessMessage);
  }, [data]);

  useEffect(() => {
    if (isError) {
      dispatch(showNotification({ theme: 'warning', summary: 'Something went wrong. Please try again.' }));
      ErrorLogService.logError(error ?? 'Share survey error', 'useShareSurvey', 'useEffect-isError');
    }
  }, [isError]);

  function handleShare(): void {
    const url = window.location.href;

    if (url.indexOf('share-id') >= 0 ? url : '') {
      shareUrl.current = url;
    }

    if (shareUrl.current) {
      toClipboard(shareUrl.current, copySuccessMessage);
    } else {
      surveyId && void shareSurvey(surveyId);
    }
  }

  function createUrl(shareId: string): string {
    return `${websiteUrl}/share-survey?share-id=${shareId}`;
  }

  return {
    canShare,
    handleShare,
    isLoading,
  };
};

export default useShareSurvey;
